import { z } from 'zod';
import { codiceFiscaleSchema } from './codiceFiscale.js';
import { ibanAdeSchema } from './iban.js';
/**
 * Contratto di locazione — coerenza dei campi principali.
 *
 * Questo schema valida:
 *  - canoneAnnuale ≈ canoneMensile * 12 (tolleranza ±1%)
 *  - durata minima/massima coerente con la tipologia contrattuale
 *  - dataStipula non futura
 *  - codici fiscali proprietario e inquilino validi (via codiceFiscaleSchema)
 *
 * Riferimenti legge:
 *  - 4+4: locazione abitativa ordinaria (L. 431/98 art. 2 c.1)
 *  - 3+2: canone concordato (L. 431/98 art. 2 c.3)
 *  - transitorio: L. 431/98 art. 5 c.1 (max 18 mesi)
 *  - studenti universitari: L. 431/98 art. 5 c.2 (6-36 mesi)
 *  - commerciale: L. 392/78 (minimo 6 anni)
 *  - comodato: nessun vincolo di durata
 */
export const tipologiaContrattoSchema = z.enum([
    '4+4',
    '3+2',
    'transitorio',
    'studente',
    'commerciale',
    'comodato',
]);
/**
 * Tabella legale delle durate min/max per tipologia contrattuale.
 *
 * Esportata (F-004) perché `packages/shared/src/schemas/adempimento.ts` deve
 * consultare la STESSA tabella per validare la durata delle proroghe, senza
 * forkarla. Non modificare la forma senza aggiornare entrambi i consumatori.
 */
export const DURATA_PER_TIPOLOGIA = {
    '4+4': { min: 48 },
    '3+2': { min: 36 },
    transitorio: { min: 1, max: 18 },
    studente: { min: 6, max: 36 },
    commerciale: { min: 72 },
    comodato: { min: 1 },
};
/** Schema dei campi "grezzi" del contratto, prima dei refinement cross-field. */
export const contractBaseSchema = z.object({
    canoneMensile: z
        .number({ required_error: 'Canone mensile obbligatorio' })
        .positive({ message: 'Il canone mensile deve essere positivo' })
        .refine((n) => Math.round(n * 100) === n * 100, {
        message: 'Il canone mensile deve avere al massimo 2 decimali',
    }),
    canoneAnnuale: z
        .number({ required_error: 'Canone annuale obbligatorio' })
        .positive({ message: 'Il canone annuale deve essere positivo' }),
    durataMesi: z
        .number({ required_error: 'Durata obbligatoria' })
        .int({ message: 'La durata deve essere un numero intero di mesi' })
        .positive({ message: 'La durata deve essere positiva' }),
    tipologia: tipologiaContrattoSchema,
    dataStipula: z.coerce.date({
        required_error: 'Data stipula obbligatoria',
        invalid_type_error: 'Data stipula non valida',
    }),
    codiceFiscaleProprietario: codiceFiscaleSchema,
    codiceFiscaleInquilino: codiceFiscaleSchema,
});
/**
 * Coerenza canone mensile <-> annuale (±1%, minimo assoluto 0.01).
 *
 * Estratta (F-004) da `applyContractRefinements` come funzione pura riusabile:
 * `adempimento.ts` la richiama per validare il nuovo canone di una proroga.
 * Accetta solo i due campi necessari (non l'intero output dello schema) così da
 * essere riusabile da schemi con shape diverse. La logica di tolleranza e il
 * messaggio sono identici all'originale — refactor puro, zero cambio di
 * comportamento.
 */
export function validateCanoneCoerenza(data, ctx) {
    const atteso = data.canoneMensile * 12;
    const tolleranza = Math.max(atteso * 0.01, 0.01);
    if (Math.abs(data.canoneAnnuale - atteso) > tolleranza) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['canoneAnnuale'],
            message: `Canone annuale incoerente: atteso ~${atteso.toFixed(2)} (mensile x12), ricevuto ${data.canoneAnnuale.toFixed(2)}`,
        });
    }
}
/**
 * Durata coerente con la tipologia contrattuale, secondo la tabella legale
 * `DURATA_PER_TIPOLOGIA`.
 *
 * Estratta (F-004) da `applyContractRefinements` come funzione pura riusabile:
 * `adempimento.ts` la richiama per le tipologie diverse da 4+4 / 3+2 (che hanno
 * la regola dell'incremento esatto). Accetta solo `tipologia`/`durataMesi`. Range,
 * messaggi e path sono identici all'originale — refactor puro, zero cambio di
 * comportamento.
 */
export function validateDurata(data, ctx) {
    const range = DURATA_PER_TIPOLOGIA[data.tipologia];
    if (data.durataMesi < range.min) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['durataMesi'],
            message: `Durata minima per '${data.tipologia}' è ${range.min} mesi (ricevuti ${data.durataMesi})`,
        });
    }
    if (range.max !== undefined && data.durataMesi > range.max) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['durataMesi'],
            message: `Durata massima per '${data.tipologia}' è ${range.max} mesi (ricevuti ${data.durataMesi})`,
        });
    }
}
/**
 * Cross-field refinements condivisi tra `contractSchema` (Spike 001) e
 * `contractFullSchema` (F-003.1). Estratto come helper privato per evitare
 * duplicazione — pure refactor, nessuna modifica di comportamento.
 *
 * F-004: i primi due blocchi (coerenza canone, durata) sono ora delegati a
 * `validateCanoneCoerenza`/`validateDurata` — funzioni pure esportate, riusate
 * da `adempimento.ts`. Comportamento invariato.
 */
function applyContractRefinements(data, ctx) {
    // 1) Coerenza canone mensile <-> annuale (±1%)
    validateCanoneCoerenza(data, ctx);
    // 2) Durata coerente con la tipologia
    validateDurata(data, ctx);
    // 3) Data stipula non futura (tolleranza: fine della giornata odierna locale)
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    if (data.dataStipula.getTime() > now.getTime()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dataStipula'],
            message: 'La data di stipula non può essere futura',
        });
    }
    // 4) Proprietario != inquilino (sanity check base)
    if (data.codiceFiscaleProprietario === data.codiceFiscaleInquilino) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['codiceFiscaleInquilino'],
            message: 'Il codice fiscale dell\'inquilino non può coincidere con quello del proprietario',
        });
    }
}
/** Indirizzo strutturato (immobile, locatore, conduttore). */
export const indirizzoSchema = z.object({
    via: z.string().min(1, 'Via obbligatoria'),
    civico: z.string().min(1, 'Civico obbligatorio'),
    cap: z.string().regex(/^\d{5}$/, 'CAP deve essere di 5 cifre'),
    comune: z.string().min(1, 'Comune obbligatorio'),
    provincia: z.string().length(2, 'Provincia deve essere di 2 caratteri').toUpperCase(),
});
/** Anagrafica base parte (locatore o conduttore). */
export const partyBaseSchema = z.object({
    codiceFiscale: codiceFiscaleSchema,
    nome: z.string().min(1, 'Nome obbligatorio'),
    cognome: z.string().min(1).optional(),
    ragioneSociale: z.string().min(1).optional(),
    indirizzo: indirizzoSchema,
});
/** Dati catastali immobile. */
export const datiCatastaliSchema = z.object({
    foglio: z.string().min(1, 'Foglio obbligatorio'),
    particella: z.string().min(1, 'Particella obbligatoria'),
    subalterno: z.string().optional(),
    rendita: z.number().positive('Rendita deve essere positiva'),
    categoria: z.string().optional(),
});
/** Regime fiscale ammissibile per il contratto. */
export const regimeFiscaleSchema = z.enum(['CEDOLARE_SECCA', 'ORDINARIO', 'CONCORDATO']);
export const contractSchema = contractBaseSchema.superRefine(applyContractRefinements);
/**
 * Forma estesa (object) della pratica completa, prima dei cross-field refinement.
 * Esposta per consentire `.deepPartial()` (vedi `contractDraftSchema`) — Zod non
 * permette `.deepPartial()` su un `ZodEffects` (output di `.superRefine`).
 */
export const contractFullBaseSchema = contractBaseSchema.extend({
    locatore: partyBaseSchema,
    conduttore: partyBaseSchema,
    immobile: indirizzoSchema,
    catastali: datiCatastaliSchema,
    regimeFiscale: regimeFiscaleSchema,
    ibanAdE: ibanAdeSchema,
});
/**
 * Schema completo della pratica di registrazione (F-003.1).
 * Estende `contractBaseSchema` con anagrafiche, immobile, dati catastali, regime
 * fiscale e IBAN AdE branded; ri-applica gli stessi refinement cross-field di
 * `contractSchema` tramite `applyContractRefinements`.
 */
export const contractFullSchema = contractFullBaseSchema.superRefine(applyContractRefinements);
/**
 * Variante "bozza" del `contractFullSchema` — tutti i campi opzionali via
 * `.deepPartial()`. Bypassa i cross-field refinements (intenzionale: gli
 * autosave parziali non devono fallire validazione cross-campo).
 *
 * Applicato sull'object pre-refinement (`contractFullBaseSchema`) perché Zod
 * non supporta `.deepPartial()` su `ZodEffects`.
 */
export const contractDraftSchema = contractFullBaseSchema.deepPartial();
/**
 * Maps each `SegnalazioneReason` (set by the operator in F-007 when blocking
 * a pratica) to the `ContractDraft` field paths that should be highlighted in
 * the F-005 correction form (UC-014).
 *
 * Used by:
 *  - Frontend: amber ring highlight on contested fields in the wizard
 *  - Backend: fieldDelta computation when re-submitting (POST /:id/correzione)
 *
 * Field paths reflect the actual shape of `contractFullBaseSchema` /
 * `contractDraftSchema` defined above:
 *  - top-level: `canoneMensile`, `canoneAnnuale`, `durataMesi`, `dataStipula`,
 *    `codiceFiscaleProprietario`, `codiceFiscaleInquilino`, `tipologia`,
 *    `regimeFiscale`, `ibanAdE`
 *  - `locatore` / `conduttore` (partyBaseSchema): `codiceFiscale`, `nome`,
 *    `cognome`, `ragioneSociale`, `indirizzo.{via,civico,cap,comune,provincia}`
 *  - `immobile` (indirizzoSchema directly): `via`, `civico`, `cap`, `comune`,
 *    `provincia`
 *  - `catastali` (datiCatastaliSchema): `foglio`, `particella`, `subalterno`,
 *    `rendita`, `categoria`
 *
 * NB: `pdfS3Key` is not a `ContractDraft` field but lives on the `Pratica`
 * row — the frontend handles it as a separate "PDF replace" affordance.
 */
export const BLOCKED_REASON_FIELDS = {
    DOCUMENTO_MANCANTE: ['pdfS3Key'],
    DOCUMENTO_ILLEGGIBILE: ['pdfS3Key'],
    DATI_ERRATI: [
        'locatore.codiceFiscale', 'locatore.nome', 'locatore.cognome',
        'conduttore.codiceFiscale', 'conduttore.nome', 'conduttore.cognome',
        'immobile.via', 'immobile.civico', 'immobile.comune',
        'codiceFiscaleProprietario', 'codiceFiscaleInquilino',
    ],
    CONTRATTO_INCOMPLETO: [
        'catastali.foglio', 'catastali.particella', 'catastali.subalterno',
        'canoneMensile', 'canoneAnnuale',
        'dataStipula', 'durataMesi',
    ],
    CODICE_FISCALE_ERRATO: [
        'locatore.codiceFiscale', 'conduttore.codiceFiscale',
        'codiceFiscaleProprietario', 'codiceFiscaleInquilino',
    ],
    ALTRO: [],
};
//# sourceMappingURL=contract.js.map